// ─────────────────────────────────────────────
// src/components/UI.jsx
// Shared, reusable UI primitives used across
// every page and panel in the app.
// ─────────────────────────────────────────────
import { useState, useRef, useEffect, forwardRef } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { STARS_DATA } from "../data/constants";

// ── Animated star background ──────────────────
export function StarField({ count = 70 }) {
  return (
    <>
      {STARS_DATA.slice(0, count).map((s) => (
        <div
          key={s.id}
          style={{
            position: "absolute",
            borderRadius: "50%",
            background: "#fff",
            width: s.size,
            height: s.size,
            top: `${s.y}%`,
            left: `${s.x}%`,
            opacity: 0.5,
            animation: `twinkle ${2 + s.delay}s ease-in-out infinite`,
            animationDelay: `${s.delay}s`,
            pointerEvents: "none",
          }}
        />
      ))}
    </>
  );
}

// ── Colored pill badge ─────────────────────────
export function Badge({ color, children, small }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: color + "22",
        color,
        border: `1px solid ${color}44`,
        borderRadius: 50,
        padding: small ? "2px 10px" : "4px 14px",
        fontSize: small ? "0.72rem" : "0.78rem",
        fontWeight: 800,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

// ── Glass-style card with hover lift ──────────
export function GlassCard({ children, style = {}, hover = true, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hover && setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: "rgba(255,255,255,0.05)",
        border: "1.5px solid rgba(255,255,255,0.12)",
        borderRadius: 20,
        padding: "1.75rem",
        transition: "transform 0.25s, box-shadow 0.25s, border-color 0.25s",
        transform: hov ? "translateY(-6px)" : "none",
        boxShadow: hov ? "0 20px 50px rgba(0,0,0,0.35)" : "none",
        borderColor: hov ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.12)",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Text input or textarea ─────────────────────
export function FormInput({ label, type = "text", value, onChange, placeholder, required, half, rows }) {
  const isTextarea = type === "textarea";
  const baseStyle = {
    background: "rgba(255,255,255,0.07)",
    border: "1.5px solid rgba(255,255,255,0.14)",
    borderRadius: 12,
    padding: "11px 15px",
    color: "#fff",
    fontSize: "0.93rem",
    fontFamily: "inherit",
    outline: "none",
    transition: "border-color 0.2s",
    width: "100%",
  };
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        flex: half ? "1 1 calc(50% - 8px)" : "1 1 100%",
        minWidth: half ? 140 : undefined,
      }}
    >
      {label && (
        <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "rgba(255,255,255,0.55)", letterSpacing: "0.04em" }}>
          {label}{required ? " *" : ""}
        </label>
      )}
      {isTextarea ? (
        <textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows || 4}
          style={{ ...baseStyle, resize: "vertical" }}
          onFocus={(e) => (e.target.style.borderColor = "#FFD93D")}
          onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.14)")}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={baseStyle}
          onFocus={(e) => (e.target.style.borderColor = "#FFD93D")}
          onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.14)")}
        />
      )}
    </div>
  );
}

// ── Dropdown select ────────────────────────────
export function FormSelect({ label, value, onChange, options, placeholder }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && (
        <label style={{ fontSize: "0.8rem", fontWeight: 700, color: "rgba(255,255,255,0.55)", letterSpacing: "0.04em" }}>
          {label}
        </label>
      )}
      <select
        value={value}
        onChange={onChange}
        style={{
          background: "#0e1240",
          border: "1.5px solid rgba(255,255,255,0.18)",
          borderRadius: 12,
          padding: "11px 42px 11px 15px",
          color: value ? "#fff" : "rgba(255,255,255,0.35)",
          fontSize: "0.93rem",
          fontFamily: "inherit",
          outline: "none",
          cursor: "pointer",
          appearance: "none",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23FF6B35' stroke-width='2' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 14px center",
        }}
      >
        <option value="">{placeholder || "Select..."}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ── Password strength checklist ────────────────
export function getPasswordChecks(password = "") {
  return {
    length: password.length >= 8,
    lower:  /[a-z]/.test(password),
    upper:  /[A-Z]/.test(password),
    digit:  /[0-9]/.test(password),
    symbol: /[^A-Za-z0-9]/.test(password),
  };
}

export function isPasswordStrong(password = "") {
  const c = getPasswordChecks(password);
  return c.length && c.lower && c.upper && c.digit && c.symbol;
}

const PASSWORD_RULES = [
  ["length", "At least 8 characters"],
  ["lower",  "One lowercase letter"],
  ["upper",  "One uppercase letter"],
  ["digit",  "One number"],
  ["symbol", "One symbol"],
];

export function PasswordChecklist({ password = "" }) {
  const checks = getPasswordChecks(password);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 2, width: "100%" }}>
      {PASSWORD_RULES.map(([key, label]) => (
        <div
          key={key}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            fontSize: "0.76rem",
            fontWeight: 600,
            color: checks[key] ? "#22C55E" : "rgba(255,255,255,0.35)",
            transition: "color 0.15s",
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontSize: "0.8rem", flexShrink: 0 }}>{checks[key] ? "✓" : "○"}</span>
          {label}
        </div>
      ))}
    </div>
  );
}

// ── hCaptcha that shrinks proportionally to fit its container,
// instead of switching to hCaptcha's separate "compact" layout ──
const HCAPTCHA_NATIVE_WIDTH = 303;
const HCAPTCHA_NATIVE_HEIGHT = 78;

export const ResponsiveHCaptcha = forwardRef(function ResponsiveHCaptcha({ sitekey, theme = "dark", onVerify, onExpire }, ref) {
  const outerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    function recalc() {
      setScale(Math.min(1, el.offsetWidth / HCAPTCHA_NATIVE_WIDTH));
    }
    recalc();
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={outerRef} style={{ width: "100%" }}>
      <div style={{ width: HCAPTCHA_NATIVE_WIDTH * scale, height: HCAPTCHA_NATIVE_HEIGHT * scale, margin: "0 auto", overflow: "hidden" }}>
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
          <HCaptcha ref={ref} sitekey={sitekey} theme={theme} onVerify={onVerify} onExpire={onExpire} />
        </div>
      </div>
    </div>
  );
});

// ── Multi-variant button ───────────────────────
const VARIANTS = {
  primary:   { background: "linear-gradient(135deg,#FF6B6B,#FF8E53)", color: "#fff", border: "none", boxShadow: "0 4px 18px rgba(255,107,107,0.4)" },
  secondary: { background: "transparent", color: "#FFD93D", border: "2px solid #FFD93D" },
  ghost:     { background: "rgba(255,255,255,0.07)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.14)" },
  success:   { background: "linear-gradient(135deg,#22C55E,#16A34A)", color: "#fff", border: "none", boxShadow: "0 4px 18px rgba(34,197,94,0.35)" },
  purple:    { background: "linear-gradient(135deg,#9B5CF6,#7C3AED)", color: "#fff", border: "none", boxShadow: "0 4px 18px rgba(155,92,246,0.4)" },
  amber:     { background: "linear-gradient(135deg,#F59E0B,#D97706)", color: "#fff", border: "none", boxShadow: "0 4px 18px rgba(245,158,11,0.4)" },
  danger:    { background: "linear-gradient(135deg,#EF4444,#DC2626)", color: "#fff", border: "none" },
};

export function Btn({ children, onClick, variant = "primary", style = {}, disabled }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: "12px 26px",
        borderRadius: 50,
        fontFamily: "inherit",
        fontSize: "0.93rem",
        fontWeight: 800,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "transform 0.18s, opacity 0.18s",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        ...VARIANTS[variant],
        transform: hov && !disabled ? "translateY(-2px) scale(1.02)" : "none",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// ── Section label (small uppercase eyebrow) ───
export function SectionLabel({ color = "#FFD93D", children }) {
  return (
    <div style={{ color, fontWeight: 800, fontSize: "0.8rem", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 8 }}>
      {children}
    </div>
  );
}

// ── Section heading ────────────────────────────
export function SectionTitle({ children, center }) {
  return (
    <h2
      style={{
        fontFamily: "'Fredoka One', cursive",
        fontSize: "clamp(1.75rem, 4vw, 2.6rem)",
        textAlign: center ? "center" : "left",
        lineHeight: 1.15,
        marginBottom: "1rem",
      }}
    >
      {children}
    </h2>
  );
}

// ── Grade options reused in forms ──────────────
export const GRADE_OPTIONS = [
  "Kindergarten","1st Grade","2nd Grade","3rd Grade","4th Grade","5th Grade",
].map((g) => ({ value: g, label: g }));
